import * as Icons from "@nova/component-icons"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { MissionTableItem } from "@nova/schemas"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { clearSelectedMissionIds, setRenamingGroupId } from "../redux/missions-table-slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    getGenericDangerConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useDeleteGroup, useUngroupMissions } from "./PilotTrackingMissionQueries"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"

export const GroupContextMenu = ({ data }: { data: MissionTableItem[] }) => {
    const dispatch = useAppDispatch()
    const modal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()
    const groupContextMenu = useAppSelector(selectActiveContextMenu("MissionGroup"))
    const { ungroupMissions, isUngrouping } = useUngroupMissions()
    const { deleteGroup } = useDeleteGroup()
    const ungroupingLoadingText = useLoadingText(isUngrouping, "Ungrouping")

    if (!groupContextMenu || !isEditor) return null

    return (
        <ContextMenu
            sections={[
                [
                    {
                        title: "Rename",
                        onClick: () => {
                            dispatch(setRenamingGroupId(groupContextMenu.data.groupId))
                            dispatch(setActiveContextMenu(undefined))
                        },
                        Icon: Icons.Pencil,
                    },
                    {
                        title: isUngrouping ? ungroupingLoadingText : "Ungroup",
                        onClick: async () => {
                            if (isUngrouping) return
                            await ungroupMissions(groupContextMenu.data.groupId)
                            dispatch(setActiveContextMenu(undefined))
                        },
                        Icon: Icons.Group,
                    },
                ],
                [
                    {
                        title: "Delete Group",
                        onClick: () => {
                            const group = data.find(
                                (d) => d.type === "group" && d.id === groupContextMenu.data.groupId
                            ) as MissionTableItem<"group"> | undefined
                            if (!group) return
                            dispatch(setActiveContextMenu(undefined))
                            void modal.confirm({
                                RenderComponent: getGenericDangerConfirmationModalComponent({
                                    title: "Delete Group?",
                                    message: `Deleting ${group.name} deletes data from ${group.missions.length} flight${group.missions.length === 1 ? "" : "s"}, do you want to proceed?`,
                                    dangerMessage: "This cannot be undone.",
                                }),
                                asyncConfirmCallback: async () => {
                                    try {
                                        await deleteGroup(group.id)
                                        dispatch(clearSelectedMissionIds())
                                        dispatch(
                                            setGlobalNotificationContext({
                                                message: `${group.name} has been deleted.`,
                                                status: GlobalNotificationStatus.success,
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    } catch {
                                        dispatch(
                                            setGlobalNotificationContext({
                                                message: `Failed to delete ${group.name}. Please try again.`,
                                                status: GlobalNotificationStatus.error,
                                                timeoutInMs: 5000,
                                            })
                                        )
                                    }
                                },
                            })
                        },
                        Icon: Icons.Trash,
                        isDestructive: true,
                    },
                ],
            ]}
            onBlur={() => dispatch(setActiveContextMenu(undefined))}
            isFixedPosition
            top={groupContextMenu.position.top}
            left={groupContextMenu.position.left}
            keepOpenOnSelect
        />
    )
}
